/**
 * Componentes de campo reutilizables para el formulario de MBZ.
 *
 * Este archivo extrae los componentes de campo que antes estaban declarados inline
 * en ZonalGrossMarginForm para mejorar la legibilidad y reutilización.
 */

import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { AppColors } from "@/styles/colors";
import { numbersWithCommaDecimal, numbersWithDecimal } from "@/utils/reg-expressions";
import { Field, GridItem, Text } from "@chakra-ui/react";
import { Control, FieldErrors } from "react-hook-form";
import { ZonalGrossMarginCreationForm } from "../create/schema/zonal-gross-margin-creation.schema";
import { handleDecimalInputChange } from "../utils/form-input-handlers";

/**
 * Componente de título de sección para agrupar campos relacionados.
 * Muestra un borde superior y texto en gris para separar visualmente
 * las secciones del formulario (Rinde, Implantación, Otros Gastos, etc.).
 */
export const SectionTitle = ({ children }: { children: string }) => (
  <Text
    gridColumn="1 / -1"
    fontSize="12px"
    fontWeight={600}
    color={AppColors.GRAY_TEXT_ALT}
    borderTop={`1px solid ${AppColors.WHITE_BORDER_1}`}
    paddingTop="16px"
    marginTop="4px"
  >
    {children}
  </Text>
);

interface NumericFieldProps {
  name: keyof ZonalGrossMarginCreationForm;
  label: string;
  control: Control<ZonalGrossMarginCreationForm>;
  disabled: boolean;
}

/**
 * Campo de entrada numérico para valores sin formato de moneda.
 * Utilizado para rinde estimado (usa punto como separador decimal).
 *
 * Aplica validación de números decimales y normalización de entrada.
 */
export const NumericField = ({ name, label, control, disabled }: NumericFieldProps) => (
  <InputField
    inputName={name}
    labelText={label}
    placeholder="Ingresar"
    control={control as any}
    disabled={disabled}
    allowedPattern={numbersWithDecimal}
    onChange={handleDecimalInputChange}
    required
    minHeight="40px"
  />
);

/**
 * Campo de entrada numérico con formato es-AR (coma como separador decimal).
 * Utilizado para alquiler y gasto de cosecha.
 *
 * Aplica validación de números decimales con coma (formato argentino).
 */
export const NumericFieldWithComma = ({ name, label, control, disabled }: NumericFieldProps) => (
  <InputField
    inputName={name}
    labelText={label}
    placeholder="Ingresar"
    control={control as any}
    disabled={disabled}
    allowedPattern={numbersWithCommaDecimal}
    required
    minHeight="40px"
  />
);

interface CurrencyFieldProps {
  name: keyof ZonalGrossMarginCreationForm;
  label: string;
  control: Control<ZonalGrossMarginCreationForm>;
  disabled: boolean;
  errors: FieldErrors<ZonalGrossMarginCreationForm>;
  prefix?: string;
  required?: boolean;
}

/**
 * Campo de entrada con formato de moneda.
 * Utilizado para todos los precios en USD/ha (agroquímicos, fertilizantes, etc.).
 *
 * Muestra el prefijo de moneda (U$S o $) y maneja errores de validación.
 */
export const CurrencyField = ({
  name,
  label,
  control,
  disabled,
  errors,
  prefix = "U$S ",
  required = true,
}: CurrencyFieldProps) => (
  <Field.Root invalid={!!errors?.[name]}>
    <Field.Label>
      <Text fontWeight={600}>{label}</Text>
      {required && (
        <Text fontWeight={600} color={AppColors.REQUIRED}>
          *
        </Text>
      )}
    </Field.Label>
    <InputCurrency
      name={name}
      control={control as any}
      disabled={disabled}
      placeholder="Ingresar"
      prefix={prefix}
      height="40px"
      hasError={!!errors?.[name]}
    />
    <Field.ErrorText>{String(errors?.[name]?.message ?? "")}</Field.ErrorText>
  </Field.Root>
);

interface SelectFieldProps {
  name: keyof ZonalGrossMarginCreationForm;
  label: string;
  items: { label: string; value: string }[];
  control: Control<ZonalGrossMarginCreationForm>;
  disabled: boolean;
  loading: boolean;
  errors: FieldErrors<ZonalGrossMarginCreationForm>;
}

/**
 * Campo de selección múltiple para entidades productivas.
 * Utilizado para zona, grano y campaña productiva.
 *
 * Configurado como multi-select para consistencia con otros selects del sistema,
 * aunque en MBZ solo se selecciona un valor a la vez.
 */
export const SelectField = ({
  name,
  label,
  items,
  control,
  disabled,
  loading,
  errors,
}: SelectFieldProps) => (
  <GridItem>
    <Field.Root invalid={!!errors?.[name]}>
      <SelectComponent
        name={name}
        items={items}
        labelText={label}
        placeholder="Seleccionar"
        control={control as any}
        clearable
        disabled={disabled}
        loading={loading}
        minHeight="40px"
        required
        fullWidth
        closeOnSelect={false}
        multiple={false}
      />
      <Field.ErrorText>{String(errors?.[name]?.message ?? "")}</Field.ErrorText>
    </Field.Root>
  </GridItem>
);
